<template>
  <div class="jobs-container">
    <!-- Search + Sort + Refresh toolbar -->
    <div class="jobs-toolbar" :class="{ 'is-search-collapsed': isMobile && !searchOpen }">
      <button
        v-if="isMobile"
        type="button"
        class="btn-search-toggle"
        :class="{ active: searchOpen || !!searchQuery }"
        :aria-expanded="searchOpen ? 'true' : 'false'"
        :aria-label="searchOpen ? 'Hide search' : 'Show search'"
        :title="searchOpen ? 'Hide search' : 'Search jobs'"
        @click="searchOpen = !searchOpen"
      >
        <i class="fa fa-search" aria-hidden="true"></i>
        <span v-if="searchQuery && !searchOpen" class="search-toggle-dot" aria-hidden="true"></span>
      </button>
      <div v-show="!isMobile || searchOpen" class="search-bar glass-input">
        <i class="fa fa-search search-icon"></i>
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search jobs..."
          ref="searchInput"
          @keydown.esc="onSearchEsc"
        />
        <button
          v-if="isMobile && searchQuery"
          type="button"
          class="btn-clear-search"
          aria-label="Clear search"
          @click="clearSearch"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>
      <div class="sort-control">
        <label for="jobs-sort" class="sort-label">Sort</label>
        <select id="jobs-sort" v-model="sortKey" class="sort-select glass-input" aria-label="Sort jobs">
          <option value="date:desc">Date (newest)</option>
          <option value="date:asc">Date (oldest)</option>
          <option value="score:desc">Match (high → low)</option>
          <option value="score:asc">Match (low → high)</option>
          <option value="salary:desc">Salary (high → low)</option>
          <option value="salary:asc">Salary (low → high)</option>
          <option value="source:asc">Source (A–Z)</option>
          <option value="source:desc">Source (Z–A)</option>
          <option value="type:asc">Type (A–Z)</option>
          <option value="type:desc">Type (Z–A)</option>
        </select>
      </div>
      <button type="button" class="btn-refresh" @click="$emit('refresh-jobs')" :disabled="loadingJobs" title="Refresh jobs" aria-label="Refresh jobs">
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loadingJobs }"></i>
      </button>
    </div>

    <div
      v-if="tableHasOverflow && filteredJobs.length > 0"
      class="table-scroll-caption"
      aria-live="polite"
    >
      <span v-if="hasOverflowY" class="caption-item">
        <i class="fas fa-arrows-alt-v" aria-hidden="true"></i>
        Scroll up/down for more jobs
      </span>
      <span v-if="hasOverflowY && hasOverflowX" class="caption-divider" aria-hidden="true">·</span>
      <span v-if="hasOverflowX" class="caption-item">
        <i class="fas fa-arrows-alt-h" aria-hidden="true"></i>
        Scroll left/right for all columns
      </span>
    </div>

    <!-- Empty State -->
    <div v-if="filteredJobs.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fa fa-briefcase"></i>
      </div>
      <h3>No job applications yet</h3>
      <p>Use the ResyMaid Chrome extension to save jobs and generate tailored resumes.</p>
    </div>

    <!-- Mobile Job Cards -->
    <div v-else class="mobile-jobs-list">
      <div v-for="job in sortedJobs" :key="'mobile-' + job._id" class="mobile-job-card">
        <div class="mobile-card-header">
          <div class="mobile-card-title">
            <h4>{{ job.title }}</h4>
            <p>{{ job.companyName || 'Unknown Company' }}</p>
          </div>
          <button @click="confirmUnsave(job)" class="mobile-card-star" title="Unsave Job">
            <i class="fa fa-star"></i>
          </button>
        </div>
        <div class="mobile-card-meta">
          <div class="mobile-card-badges">
            <span 
              v-if="job.compatibilityScore?.overallScore != null" 
              class="match-badge mobile-match"
              :class="getMatchClass(job.compatibilityScore.overallScore)"
              @click="showMobileMatchModal(job)"
            >
              {{ job.compatibilityScore.overallScore }}% Match
            </span>
            <span v-else-if="job.compatibilityScore" class="match-unavailable mobile-match-unavailable">
              {{ job.compatibilityScore.overallVerdict || "—" }}
            </span>
            <span v-if="job.salary" class="salary-badge mobile-salary">{{ job.salary }}</span>
            <span v-if="job.jobType" class="job-type-badge mobile-job-type">{{ job.jobType }}</span>
            <span :class="['source-badge', `source-${(job.sourceSite || 'web').toLowerCase()}`]">
              {{ job.sourceSite || 'Web' }}
            </span>
          </div>
          <p class="date-text">Saved {{ formatDate(job.createdAt) }}</p>
        </div>
        <div class="mobile-card-row">
          <label class="status-field mobile-status">
            <span class="status-field-label">Status</span>
            <select
              class="status-select"
              :class="'status-' + effectiveStatus(job)"
              :value="effectiveStatus(job)"
              :disabled="disabled"
              @change="updateJobStatus(job, $event.target.value)"
            >
              <option
                v-for="opt in statusOptionsForJob(job)"
                :key="opt.value"
                :value="opt.value"
                :disabled="opt.disabled"
              >
                {{ opt.label }}{{ opt.disabled ? ' (generate resume first)' : '' }}
              </option>
            </select>
          </label>
          <button
            type="button"
            @click="openNotesModal(job)"
            class="btn-notes-icon"
            :class="{ 'has-notes': !!job.notes }"
            :title="job.notes ? 'View/Edit Notes' : 'Add Notes'"
            aria-label="Notes"
          >
            <i class="fa fa-sticky-note" aria-hidden="true"></i>
          </button>
        </div>
        <div class="mobile-card-actions">
          <button v-if="!job.hasResume" @click="generateResume(job)" class="btn btn-generate" :disabled="generatingJobId === job._id">
            <i v-if="generatingJobId === job._id" class="fa fa-spinner fa-spin" style="margin-right: 6px;"></i>
            <i v-else class="fa fa-wand-magic-sparkles" style="margin-right: 6px;"></i>
            {{ generatingJobId === job._id ? 'Generating...' : 'Generate' }}
          </button>
          <button v-else @click="openDownloadModal(job)" class="btn btn-download">
            <i class="fa fa-download" style="margin-right: 6px;"></i>
            Download
          </button>
          <a
            v-if="job.url"
            :href="job.url"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-view-job"
            title="View job posting"
          >
            <i class="fa fa-external-link-alt" aria-hidden="true"></i>
            <span>View</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Jobs Table (Desktop) -->
    <div
      v-if="filteredJobs.length > 0"
      class="table-scroll-region"
      :class="{
        'has-overflow-y': hasOverflowY,
        'has-overflow-x': hasOverflowX,
        'scrolled-x': tableScrolledX,
      }"
    >
      <div
        ref="tableWrapper"
        class="table-wrapper"
        :class="{ 'is-scrolled-y': tableScrolled }"
        @scroll="onTableScroll"
      >
      <table class="jobs-table">
        <thead>
          <tr>
            <th class="save-col"></th>
            <th>Job Title</th>
            <th>Company</th>
            <th
              class="sortable"
              :class="{ active: sortBy === 'score' }"
              @click="setSort('score')"
            >
              Match <i class="fa sort-icon" :class="sortIcon('score')"></i>
            </th>
            <th
              class="sortable"
              :class="{ active: sortBy === 'type' }"
              @click="setSort('type')"
            >
              Type <i class="fa sort-icon" :class="sortIcon('type')"></i>
            </th>
            <th
              class="sortable"
              :class="{ active: sortBy === 'salary' }"
              @click="setSort('salary')"
            >
              Salary <i class="fa sort-icon" :class="sortIcon('salary')"></i>
            </th>
            <th>Status</th>
            <th>Notes</th>
            <th
              class="sortable"
              :class="{ active: sortBy === 'source' }"
              @click="setSort('source')"
            >
              Source <i class="fa sort-icon" :class="sortIcon('source')"></i>
            </th>
            <th
              class="sortable"
              :class="{ active: sortBy === 'date' }"
              @click="setSort('date')"
            >
              Date <i class="fa sort-icon" :class="sortIcon('date')"></i>
            </th>
            <th>Resume</th>
            <th>URL</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="job in sortedJobs" :key="job._id" class="job-row">
            <td class="save-cell">
              <button @click="confirmUnsave(job)" class="btn-star saved" title="Unsave Job">
                <i class="fa fa-star"></i>
              </button>
            </td>
            <td class="job-title-cell">
              <span class="job-title">{{ job.title }}</span>
              <span v-if="job.location" class="job-location">{{ job.location }}</span>
            </td>
            <td class="company-cell">{{ job.companyName || '-' }}</td>
            <td class="match-cell">
              <div 
                v-if="job.compatibilityScore?.overallScore != null" 
                class="match-badge-wrapper"
                @mouseenter="showMatchPopout(job, $event)"
                @mouseleave="hideMatchPopout"
              >
                <span 
                  class="match-badge" 
                  :class="getMatchClass(job.compatibilityScore.overallScore)"
                >
                  {{ job.compatibilityScore.overallScore }}%
                </span>
                <span class="match-verdict">{{ job.compatibilityScore.overallVerdict }}</span>
              </div>
              <div v-else-if="job.compatibilityScore" class="match-unavailable">
                {{ job.compatibilityScore.overallVerdict || "—" }}
              </div>
              <div v-else class="match-pending">
                <span class="match-loading" title="Analyzing...">
                  <i class="fa fa-circle-notch fa-spin"></i>
                </span>
              </div>
            </td>
            <td class="type-cell">
              <span v-if="job.jobType" class="job-type-badge">{{ job.jobType }}</span>
              <span v-else class="no-type">-</span>
            </td>
            <td class="salary-cell">
              <span v-if="job.salary" class="salary-badge">{{ job.salary }}</span>
              <span v-else class="no-salary">-</span>
            </td>
            <td class="status-cell">
              <select
                class="status-select"
                :class="'status-' + effectiveStatus(job)"
                :value="effectiveStatus(job)"
                :disabled="disabled"
                @change="updateJobStatus(job, $event.target.value)"
                :title="!job.hasResume ? 'Ready status unlocks after you generate a resume' : ''"
              >
                <option
                  v-for="opt in statusOptionsForJob(job)"
                  :key="opt.value"
                  :value="opt.value"
                  :disabled="opt.disabled"
                >
                  {{ opt.label }}{{ opt.disabled ? ' (generate resume first)' : '' }}
                </option>
              </select>
            </td>
            <td class="notes-cell">
              <button @click="openNotesModal(job)" class="btn-notes" :title="job.notes ? 'View/Edit Notes' : 'Add Notes'">
                <i class="fa fa-sticky-note"></i>
              </button>
            </td>
            <td class="source-cell">
              <span :class="['source-badge', `source-${(job.sourceSite || 'web').toLowerCase()}`]">
                {{ job.sourceSite || 'Web' }}
              </span>
            </td>
            <td class="date-cell">{{ formatDate(job.createdAt) }}</td>
            <td class="resume-cell">
              <button v-if="!job.hasResume" @click="generateResume(job)" class="btn btn-generate" :disabled="generatingJobId === job._id">
                <i v-if="generatingJobId === job._id" class="fa fa-spinner fa-spin" style="margin-right: 6px;"></i>
                <i v-else class="fa fa-wand-magic-sparkles" style="margin-right: 6px;"></i>
                {{ generatingJobId === job._id ? 'Generating...' : 'Generate' }}
              </button>
              <button v-else @click="openDownloadModal(job)" class="btn btn-download">
                <i class="fa fa-download" style="margin-right: 6px;"></i>
                Download
              </button>
            </td>
            <td class="url-cell">
              <a v-if="job.url" :href="job.url" target="_blank" class="btn-icon" title="View Posting">
                <i class="fa fa-external-link-alt"></i>
              </a>
              <span v-else class="no-url">-</span>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
      <div v-if="canScrollDown" class="scroll-edge scroll-edge-bottom" aria-hidden="true"></div>
      <div v-if="canScrollXRight" class="scroll-edge scroll-edge-right" aria-hidden="true"></div>
      <div v-if="canScrollXLeft" class="scroll-edge scroll-edge-left" aria-hidden="true"></div>
      <button
        v-if="showScrollHintY"
        type="button"
        class="scroll-hint scroll-hint-down"
        @click="scrollTableDown"
        aria-label="Scroll table down for more jobs"
      >
        <i class="fas fa-chevron-down" aria-hidden="true"></i>
        <span>More jobs</span>
      </button>
      <button
        v-if="showScrollHintX"
        type="button"
        class="scroll-hint scroll-hint-side"
        @click="scrollTableRight"
        aria-label="Scroll table sideways for more columns"
      >
        <i class="fas fa-chevron-right" aria-hidden="true"></i>
        <span>More columns</span>
      </button>
    </div>

    <div v-if="jobsTotal > 0" class="jobs-pagination" role="navigation" aria-label="Jobs pagination">
      <span class="pagination-meta">
        Showing {{ pageRangeStart }}–{{ pageRangeEnd }} of {{ jobsTotal }}
      </span>
      <div class="pagination-controls">
        <button
          type="button"
          class="btn-page"
          :disabled="jobsPage <= 1 || loadingJobs"
          @click="goToPage(jobsPage - 1)"
        >
          Previous
        </button>
        <span class="pagination-page">Page {{ jobsPage }} / {{ totalPages }}</span>
        <button
          type="button"
          class="btn-page"
          :disabled="jobsPage >= totalPages || loadingJobs"
          @click="goToPage(jobsPage + 1)"
        >
          Next
        </button>
      </div>
    </div>

    <!-- Unsave Confirmation Modal -->
    <Teleport to="body">
      <div v-if="showDeleteModal" class="modal-overlay" @click.self="closeDeleteModal">
        <div class="modal glass-modal">
          <div class="modal-header">
            <h3>Unsave Job</h3>
            <button class="modal-close" @click="closeDeleteModal">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <p class="modal-subtitle">{{ jobToDelete?.title }}</p>
          <p class="modal-company">{{ jobToDelete?.companyName }}</p>
          <p class="delete-warning">
            <i class="fa fa-exclamation-triangle" style="color: #fbbf24; margin-right: 8px;"></i>
            Are you sure you want to unsave this job? It will be removed from your dashboard. Your generated resume will still be available in the extension when you open this job again.
          </p>
          
          <div class="modal-actions">
            <button @click="closeDeleteModal" class="btn btn-cancel">Keep Job</button>
            <button @click="deleteJob" class="btn btn-confirm-delete" :disabled="deleting">
              <span v-if="deleting">Removing...</span>
              <span v-else>Unsave Job</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Download Modal -->
    <Teleport to="body">
      <div v-if="showDownloadModal" class="modal-overlay" @click.self="closeDownloadModal">
      <div class="modal glass-modal">
        <div class="modal-header">
          <h3>Download Resume</h3>
          <button class="modal-close" @click="closeDownloadModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <p class="modal-subtitle">{{ selectedJob?.title }}</p>
        <p class="modal-company">{{ selectedJob?.companyName }}</p>
        
        <div class="download-options">
          <button
            type="button"
            :disabled="downloadInProgress !== null"
            @click="downloadResume(selectedJob._id, 'pdf')"
            class="download-btn"
            :class="{ 'is-loading': downloadInProgress === 'pdf' }"
          >
            <div class="download-icon pdf">
              <i v-if="downloadInProgress === 'pdf'" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa fa-file-pdf"></i>
            </div>
            <span>{{ downloadInProgress === 'pdf' ? 'Preparing…' : 'PDF' }}</span>
          </button>
          <button
            type="button"
            :disabled="downloadInProgress !== null"
            @click="downloadResume(selectedJob._id, 'docx')"
            class="download-btn"
            :class="{ 'is-loading': downloadInProgress === 'docx' }"
          >
            <div class="download-icon docx">
              <i v-if="downloadInProgress === 'docx'" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa fa-file-word"></i>
            </div>
            <span>{{ downloadInProgress === 'docx' ? 'Preparing…' : 'DOCX' }}</span>
          </button>
          <button
            type="button"
            :disabled="downloadInProgress !== null"
            @click="downloadResume(selectedJob._id, 'html')"
            class="download-btn"
            :class="{ 'is-loading': downloadInProgress === 'html' }"
          >
            <div class="download-icon html">
              <i v-if="downloadInProgress === 'html'" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa fa-code"></i>
            </div>
            <span>{{ downloadInProgress === 'html' ? 'Preparing…' : 'HTML' }}</span>
          </button>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- Notes Modal — compact Apple-style sheet -->
    <Teleport to="body">
      <div
        v-if="showNotesModal"
        class="modal-overlay notes-overlay"
        @click.self="closeNotesModal"
      >
        <div
          class="notes-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notes-sheet-title"
        >
          <div class="notes-sheet-handle" aria-hidden="true"></div>
          <div class="notes-sheet-toolbar">
            <h3 id="notes-sheet-title" class="notes-sheet-title">Notes</h3>
            <button
              type="button"
              class="notes-done-btn"
              @click="closeNotesModal"
              :disabled="savingNotes"
            >
              <span v-if="savingNotes">Saving…</span>
              <span v-else>Done</span>
            </button>
          </div>
          <p class="notes-sheet-context">
            <span class="notes-sheet-job">{{ notesJob?.title }}</span>
            <span v-if="notesJob?.companyName" class="notes-sheet-sep">·</span>
            <span v-if="notesJob?.companyName" class="notes-sheet-company">{{ notesJob.companyName }}</span>
          </p>
          <textarea
            ref="notesTextarea"
            v-model="notesText"
            class="notes-sheet-input"
            placeholder="Write a note…"
            rows="3"
            @input="queueNotesSave"
          ></textarea>
          <p
            v-if="savingNotes || notesSaveError || notesSavedFlash || notesText !== notesInitial"
            class="notes-save-status"
            :class="{ error: notesSaveError }"
            aria-live="polite"
          >
            <span v-if="savingNotes">Saving…</span>
            <span v-else-if="notesSaveError">{{ notesSaveError }}</span>
            <span v-else-if="notesText !== notesInitial">Unsaved changes</span>
            <span v-else-if="notesSavedFlash">Saved</span>
          </p>
        </div>
      </div>
    </Teleport>

    <!-- Mobile Match Modal -->
    <Teleport to="body">
      <div v-if="showMatchModal && matchModalJob?.compatibilityScore" class="modal-overlay" @click.self="closeMatchModal">
        <div class="modal glass-modal match-modal">
          <div class="modal-header">
            <h3>Match Analysis</h3>
            <button class="modal-close" @click="closeMatchModal">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <p class="modal-subtitle">{{ matchModalJob?.title }}</p>
          <p class="modal-company">{{ matchModalJob?.companyName }}</p>
          <div class="match-modal-content">
            <div class="match-modal-header">
              <span class="match-modal-score" :class="getMatchClass(matchModalJob.compatibilityScore.overallScore)">
              {{ matchModalJob.compatibilityScore.overallScore }}%
            </span>
            <span class="match-modal-verdict" :class="getMatchClass(matchModalJob.compatibilityScore.overallScore)">
              {{ matchModalJob.compatibilityScore.overallVerdict }}
            </span>
          </div>

          <div class="match-modal-categories">
            <div 
              v-for="category in matchModalJob.compatibilityScore.categories" 
              :key="category.name"
              class="match-modal-category"
            >
              <div class="category-header">
                <span class="category-name">{{ category.name }}</span>
                <span class="category-score">{{ category.score }}%</span>
              </div>
              <div class="category-bar">
                <div 
                  class="category-fill" 
                  :style="{ width: category.score + '%', background: getCategoryColor(category.score) }"
                ></div>
              </div>
            </div>
            </div>
            <div v-if="matchModalJob.compatibilityScore.highlights?.length" class="match-modal-section">
            <div class="match-modal-label">Highlights</div>
            <div 
              v-for="(highlight, idx) in matchModalJob.compatibilityScore.highlights" 
              :key="'h-' + idx"
              class="match-modal-item highlight"
            >
              <i class="fa fa-check-circle"></i>
              <span>{{ highlight }}</span>
            </div>
          </div>

          <div v-if="matchModalJob.compatibilityScore.gaps?.length" class="match-modal-section">
            <div class="match-modal-label">Gaps</div>
            <div 
              v-for="(gap, idx) in matchModalJob.compatibilityScore.gaps" 
              :key="'g-' + idx"
              class="match-modal-item gap"
            >
              <i class="fa fa-exclamation-triangle"></i>
              <span>{{ gap }}</span>
            </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Match Popout -->
    <Teleport to="body">
      <Transition name="popout">
        <div 
          v-if="matchPopout.visible && matchPopout.job?.compatibilityScore" 
          class="match-popout"
          :style="{
            left: matchPopout.x + 'px',
            top: matchPopout.placement === 'below' ? matchPopout.y + 'px' : 'auto',
            bottom: matchPopout.placement === 'above' ? matchPopout.bottom + 'px' : 'auto',
            height: matchPopout.maxHeight + 'px',
            maxHeight: matchPopout.maxHeight + 'px'
          }"
          @mouseenter="matchPopout.visible = true"
          @mouseleave="hideMatchPopout"
        >
          <div class="popout-header">
            <span class="popout-score" :class="getMatchClass(matchPopout.job.compatibilityScore.overallScore)">
              {{ matchPopout.job.compatibilityScore.overallScore }}%
            </span>
            <span class="popout-verdict" :class="getMatchClass(matchPopout.job.compatibilityScore.overallScore)">
              {{ matchPopout.job.compatibilityScore.overallVerdict }}
            </span>
          </div>

          <div class="popout-body">
          <div class="popout-categories">
            <div 
              v-for="category in matchPopout.job.compatibilityScore.categories" 
              :key="category.name"
              class="popout-category"
            >
              <div class="category-header">
                <span class="category-name">{{ category.name }}</span>
                <span class="category-score">{{ category.score }}%</span>
              </div>
              <div class="category-bar">
                <div 
                  class="category-fill" 
                  :style="{ width: category.score + '%', background: getCategoryColor(category.score) }"
                ></div>
              </div>
            </div>
          </div>

          <div v-if="matchPopout.job.compatibilityScore.highlights?.length" class="popout-section">
            <div class="popout-label">Highlights</div>
            <div 
              v-for="(highlight, idx) in matchPopout.job.compatibilityScore.highlights.slice(0, 3)" 
              :key="'h-' + idx"
              class="popout-item highlight"
            >
              <i class="fa fa-check-circle"></i>
              <span>{{ highlight }}</span>
            </div>
          </div>

          <div v-if="matchPopout.job.compatibilityScore.gaps?.length" class="popout-section">
            <div class="popout-label">Gaps</div>
            <div 
              v-for="(gap, idx) in matchPopout.job.compatibilityScore.gaps.slice(0, 2)" 
              :key="'g-' + idx"
              class="popout-item gap"
            >
              <i class="fa fa-exclamation-triangle"></i>
              <span>{{ gap }}</span>
            </div>
          </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { inject } from "vue";
import axios, { formatApiErrorMessage } from "@/axios";
import html2pdf from "html2pdf.js";
import {
  effectiveStatus,
  statusLabel,
  statusOptionsForJob,
} from "@/utils/jobStatus";

export default {
  name: "JobsGrid",
  setup() {
    const isMobile = inject("isMobile", false);
    return { isMobile };
  },
  emits: [
    "refresh-jobs",
    "jobs-query",
    "update-job-status",
    "update-job-notes",
    "update-job-resume-generated",
    "refresh-credits",
  ],
  props: {
    jobs: {
      type: Array,
      default: () => []
    },
    jobsTotal: {
      type: Number,
      default: 0
    },
    jobsPage: {
      type: Number,
      default: 1
    },
    jobsLimit: {
      type: Number,
      default: 50
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loadingJobs: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: "",
      searchOpen: false,
      searchDebounceTimer: null,
      sortBy: "date",
      sortDir: "desc",
      suppressQueryEmit: false,
      showDownloadModal: false,
      selectedJob: null,
      downloadInProgress: null,
      showDeleteModal: false,
      jobToDelete: null,
      deleting: false,
      showNotesModal: false,
      notesJob: null,
      notesText: "",
      notesInitial: "",
      savingNotes: false,
      notesSaveError: "",
      notesSavedFlash: false,
      notesSaveTimer: null,
      notesSavePromise: null,
      notesSavedFlashTimer: null,
      // Match popout
      matchPopout: {
        visible: false,
        job: null,
        x: 0,
        y: 0,
        bottom: null,
        placement: "below",
        maxHeight: 400,
      },
      // Mobile match modal
      showMatchModal: false,
      matchModalJob: null,
      generatingJobId: null,
      canScrollDown: false,
      canScrollXRight: false,
      canScrollXLeft: false,
      hasOverflowY: false,
      hasOverflowX: false,
      tableScrolled: false,
      tableScrolledX: false,
      scrollHintDismissedY: false,
      scrollHintDismissedX: false,
      tableResizeObserver: null,
    };
  },
  computed: {
    sortKey: {
      get() {
        return `${this.sortBy}:${this.sortDir}`;
      },
      set(val) {
        const [by, dir] = String(val || "date:desc").split(":");
        this.sortBy = by || "date";
        this.sortDir = dir === "asc" ? "asc" : "desc";
        this.emitJobsQuery({ page: 1 });
      },
    },
    tableHasOverflow() {
      return this.hasOverflowY || this.hasOverflowX;
    },
    showScrollHintY() {
      return this.canScrollDown && !this.scrollHintDismissedY && !this.tableScrolled;
    },
    showScrollHintX() {
      return this.canScrollXRight && !this.scrollHintDismissedX && !this.tableScrolledX;
    },
    /** Server already filters/sorts; keep names used by the template. */
    filteredJobs() {
      return this.jobs;
    },
    sortedJobs() {
      return this.jobs;
    },
    totalPages() {
      return Math.max(1, Math.ceil((this.jobsTotal || 0) / (this.jobsLimit || 50)));
    },
    pageRangeStart() {
      if (!this.jobsTotal) return 0;
      return (this.jobsPage - 1) * this.jobsLimit + 1;
    },
    pageRangeEnd() {
      return Math.min(this.jobsTotal, this.jobsPage * this.jobsLimit);
    },
  },
  watch: {
    jobs() {
      this.$nextTick(() => this.updateTableScrollState());
    },
    loadingJobs(val) {
      if (!val) this.$nextTick(() => this.updateTableScrollState());
    },
    searchQuery() {
      if (this.suppressQueryEmit) return;
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.emitJobsQuery({ page: 1, search: this.searchQuery });
      }, 300);
    },
    searchOpen(open) {
      if (open) {
        this.$nextTick(() => this.$refs.searchInput?.focus());
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateTableScrollState();
      const el = this.$refs.tableWrapper;
      if (el && typeof ResizeObserver !== "undefined") {
        this.tableResizeObserver = new ResizeObserver(() => this.updateTableScrollState());
        this.tableResizeObserver.observe(el);
      }
    });
    window.addEventListener("resize", this.updateTableScrollState);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateTableScrollState);
    if (this.tableResizeObserver) {
      this.tableResizeObserver.disconnect();
    }
    clearTimeout(this.searchDebounceTimer);
    this.clearNotesTimers();
  },
  methods: {
    emitJobsQuery(overrides = {}) {
      this.$emit("jobs-query", {
        page: overrides.page ?? this.jobsPage,
        search: overrides.search !== undefined ? overrides.search : this.searchQuery,
        sortBy: overrides.sortBy ?? this.sortBy,
        sortDir: overrides.sortDir ?? this.sortDir,
      });
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.emitJobsQuery({ page });
    },
    clearSearch() {
      this.searchQuery = "";
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    onSearchEsc() {
      if (this.isMobile) this.searchOpen = false;
    },
    updateTableScrollState() {
      const el = this.$refs.tableWrapper;
      if (!el) return;
      const slop = 4;
      const maxScrollTop = el.scrollHeight - el.clientHeight;
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      this.hasOverflowY = maxScrollTop > slop;
      this.hasOverflowX = maxScrollLeft > slop;
      this.canScrollDown = this.hasOverflowY && el.scrollTop < maxScrollTop - slop;
      this.canScrollXRight = this.hasOverflowX && el.scrollLeft < maxScrollLeft - slop;
      this.canScrollXLeft = el.scrollLeft > slop;
      if (!this.hasOverflowY) this.tableScrolled = false;
      if (!this.hasOverflowX) this.tableScrolledX = false;
    },
    onTableScroll() {
      const el = this.$refs.tableWrapper;
      if (!el) return;
      const slop = 8;
      this.tableScrolled = el.scrollTop > slop;
      this.tableScrolledX = el.scrollLeft > slop;
      if (this.tableScrolled) this.scrollHintDismissedY = true;
      if (this.tableScrolledX) this.scrollHintDismissedX = true;
      const maxScrollTop = el.scrollHeight - el.clientHeight;
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      this.hasOverflowY = maxScrollTop > slop;
      this.hasOverflowX = maxScrollLeft > slop;
      this.canScrollDown = this.hasOverflowY && el.scrollTop < maxScrollTop - slop;
      this.canScrollXRight = this.hasOverflowX && el.scrollLeft < maxScrollLeft - slop;
      this.canScrollXLeft = el.scrollLeft > slop;
    },
    scrollTableDown() {
      const el = this.$refs.tableWrapper;
      if (!el) return;
      el.scrollBy({ top: Math.max(120, el.clientHeight * 0.6), behavior: "smooth" });
      this.scrollHintDismissedY = true;
    },
    scrollTableRight() {
      const el = this.$refs.tableWrapper;
      if (!el) return;
      el.scrollBy({ left: Math.max(160, el.clientWidth * 0.5), behavior: "smooth" });
      this.scrollHintDismissedX = true;
    },
    parseSalarySort(salaryStr) {
      if (!salaryStr || typeof salaryStr !== "string") return 0;
      const match = salaryStr.replace(/,/g, "").match(/(\d+(?:\.\d+)?)\s*[KkMm]?/);
      if (!match) return 0;
      let n = parseFloat(match[1]);
      if (/[Mm]/.test(salaryStr)) n *= 1e6;
      else if (/[Kk]/.test(salaryStr)) n *= 1e3;
      return n;
    },
    setSort(field) {
      if (this.sortBy === field) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortBy = field;
        this.sortDir = field === "date" || field === "score" || field === "salary" ? "desc" : "asc";
      }
      this.emitJobsQuery({ page: 1 });
    },
    sortIcon(field) {
      if (this.sortBy !== field) return "fa-sort";
      return this.sortDir === "asc" ? "fa-sort-up" : "fa-sort-down";
    },
    // Match score methods
    getMatchClass(score) {
      if (score >= 85) return 'match-strong';
      if (score >= 70) return 'match-good';
      if (score >= 50) return 'match-fair';
      return 'match-weak';
    },
    showMatchPopout(job, event) {
      const rect = event.target.getBoundingClientRect();
      const padding = 20;
      const popoutWidth = 380;
      const gap = 8;

      // Space from popout edge to viewport edge (popout is positioned at rect.bottom + gap when below)
      const spaceBelow = window.innerHeight - (rect.bottom + gap) - padding;
      const spaceAbove = rect.top - gap - padding;
      const placeAbove = spaceAbove > spaceBelow;

      let left = rect.left;
      if (left + popoutWidth > window.innerWidth - padding) {
        left = window.innerWidth - popoutWidth - padding;
      }
      if (left < padding) {
        left = padding;
      }

      // Popout height must fit entirely in viewport; we set height explicitly so the box never overflows
      const availableHeight = placeAbove ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(180, Math.min(availableHeight, Math.floor(window.innerHeight * 0.6)));
      const popoutTop = placeAbove ? null : rect.bottom + gap;
      const popoutBottom = placeAbove ? window.innerHeight - rect.top + gap : null;
      const popoutTopEdge = placeAbove ? (rect.top - gap - maxHeight) : (rect.bottom + gap);
      const popoutBottomEdge = placeAbove ? (rect.top - gap) : (rect.bottom + gap + maxHeight);

      // Debug: paste this output when reporting the cut-off issue
      console.log('[MatchPopout]', JSON.stringify({
        viewport: { height: window.innerHeight, width: window.innerWidth },
        triggerRect: { top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left },
        spaceBelow,
        spaceAbove,
        placeAbove,
        availableHeight,
        maxHeight,
        cap60: Math.floor(window.innerHeight * 0.6),
        position: { left, top: popoutTop, bottom: popoutBottom },
        popoutBounds: { topEdge: popoutTopEdge, bottomEdge: popoutBottomEdge },
        fitsInViewport: popoutBottomEdge <= window.innerHeight - padding && popoutTopEdge >= padding,
      }, null, 2));      this.matchPopout = {
        visible: true,
        job,
        x: left,
        y: rect.bottom + gap,
        bottom: placeAbove ? window.innerHeight - rect.top + gap : null,
        placement: placeAbove ? "above" : "below",
        maxHeight,
      };
    },
    hideMatchPopout() {
      this.matchPopout.visible = false;
    },
    showMobileMatchModal(job) {
      this.matchModalJob = job;
      this.showMatchModal = true;
    },
    closeMatchModal() {
      this.showMatchModal = false;
      this.matchModalJob = null;
    },
    getCategoryColor(score) {
      if (score >= 85) return '#22c55e';
      if (score >= 70) return '#eab308';
      if (score >= 50) return '#f97316';
      return '#ef4444';
    },
    effectiveStatus,
    statusOptionsForJob,
    formatStatus(status) {
      return statusLabel(status);
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    async generateResume(job) {
      if (!job || job.hasResume) return;
      this.generatingJobId = job._id;
      try {
        const payload = job._id ? { jobId: job._id } : { jobData: job };
        const response = await axios.post('/api/resume/generate', payload);
        const jobId = response.data?.jobId || job._id;
        this.$emit('update-job-resume-generated', {
          jobId,
          compatibilityScore: response.data?.compatibilityScore || null,
        });
        if (response.data?.creditsRemaining != null) {
          this.$emit('refresh-credits');
        }
        if (!job._id) {
          this.$emit('refresh-jobs');
        }
      } catch (error) {
        console.error('Generate resume error:', error);
        alert(formatApiErrorMessage(error, 'Failed to generate resume.'));
      } finally {
        this.generatingJobId = null;
      }
    },
    openDownloadModal(job) {
      this.selectedJob = job;
      this.showDownloadModal = true;
    },
    closeDownloadModal() {
      this.showDownloadModal = false;
      this.selectedJob = null;
      this.downloadInProgress = null;
    },
    async downloadResume(jobId, format) {
      if (this.downloadInProgress !== null) return;
      this.downloadInProgress = format;
      try {
        if (format === "pdf") {
          const ok = await this.downloadPdfWithFallback(jobId);
          if (ok) this.closeDownloadModal();
          return;
        }
        const response = await axios.get(`/api/resume/${jobId}/download/${format}`, {
          responseType: "blob",
        });
        const contentDisposition = response.headers["content-disposition"];
        let filename = `resume.${format}`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/);
          if (match) filename = match[1];
        } else {
          // Fallback: build "<First Last>_<JobTitle>_Resume.<ext>" if header missing
          const user = this.$store.getters["auth/user"];
          const fullName = user
            ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Resume"
            : "Resume";
          const safeName = fullName.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
          const job = this.jobs.find((j) => j._id === jobId) || this.selectedJob;
          const jobTitle = job?.title || job?.companyName || "Job";
          const safeTitle = jobTitle.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
          filename = `${safeName}_${safeTitle}_Resume.${format}`.replace(/_+/g, "_");
        }
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        this.closeDownloadModal();
      } catch (error) {
        console.error("Download error:", error);
        alert(formatApiErrorMessage(error, "Failed to download resume. Please try again."));
      } finally {
        this.downloadInProgress = null;
      }
    },
    async downloadPdfWithFallback(jobId) {
      try {
        const response = await axios.get(`/api/resume/${jobId}/download/pdf`, {
          responseType: "blob",
        });
        const contentDisposition = response.headers["content-disposition"];
        let filename = "resume.pdf";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/);
          if (match) filename = match[1];
        } else {
          const user = this.$store.getters["auth/user"];
          const fullName = user
            ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Resume"
            : "Resume";
          const safeName = fullName.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
          const job = this.jobs.find((j) => j._id === jobId) || this.selectedJob;
          const jobTitle = job?.title || job?.companyName || "Job";
          const safeTitle = jobTitle.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
          filename = `${safeName}_${safeTitle}_Resume.pdf`.replace(/_+/g, "_");
        }
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
      } catch (serverError) {
        const status = serverError.response?.status;
        if (status !== 500 && status !== 502 && status !== 503) {
          console.error("PDF download error:", serverError);
          alert(formatApiErrorMessage(serverError, "Failed to download PDF. Please try again."));
          return false;
        }
        return await this.downloadPdfFromHtmlFallback(jobId);
      }
    },
    async downloadPdfFromHtmlFallback(jobId) {
      let iframe = null;
      try {
        const response = await axios.get(`/api/resume/${jobId}/download/html`, {
          responseType: "text",
        });
        let htmlContent = response.data;
        const contentDisposition = response.headers["content-disposition"];
        let filename = "resume.pdf";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/);
          if (match) filename = (match[1] || "").replace(/\.html$/i, ".pdf");
        } else {
          const user = this.$store.getters["auth/user"];
          const fullName = user
            ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Resume"
            : "Resume";
          const safeName = fullName.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
          const job = this.jobs.find((j) => j._id === jobId) || this.selectedJob;
          const jobTitle = job?.title || job?.companyName || "Job";
          const safeTitle = jobTitle.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
          filename = `${safeName}_${safeTitle}_Resume.pdf`.replace(/_+/g, "_");
        }
        /* PDF: section headers bold black; only categories + job positions blue; companies bold black; margin; colon after category */
        const pdfExportOverrides = `
<style id="pdf-export-overrides">
  html, body { background: #ffffff !important; border: none !important; box-shadow: none !important; }
  .t-name { font-size: 28px !important; font-weight: 700 !important; }
  .t-section { font-size: 24px !important; color: #333 !important; font-weight: 700 !important; border-bottom: 1px solid #333 !important; margin-top: 15px !important; padding-bottom: 5px !important; }
  .t-role { font-size: 20px !important; color: #0056b3 !important; font-weight: 700 !important; margin: 10px 0 !important; }
  .t-company { font-size: 16px !important; color: #333 !important; font-weight: 700 !important; }
  .t-body, .t-meta, .t-bullet { font-size: 16px !important; }
  .t-meta { color: #666 !important; }
  .t-meta-line, .experience-item .t-meta { color: #333 !important; }
  .resume-header { margin-bottom: 20px !important; }
  .resume-section { margin-bottom: 15px !important; margin-top: 15px !important; }
  .experience-item { margin-bottom: 10px !important; }
  .experience-item ul { padding-left: 20px !important; }
  .experience-item ul li { margin-bottom: 10px !important; line-height: 1.6 !important; }
</style>`;
        htmlContent = htmlContent.replace(/<\/head>/i, pdfExportOverrides + "\n</head>");
        iframe = document.createElement("iframe");
        iframe.setAttribute("style", "position:absolute;left:-9999px;top:0;width:210mm;height:297mm;border:none;background:#fff;");
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument;
        doc.open();
        doc.write(htmlContent);
        doc.close();
        await new Promise((resolve) => setTimeout(resolve, 350));
        const body = iframe.contentDocument?.body;
        if (!body) {
          alert("Failed to generate PDF from resume.");
          return false;
        }
        const root = iframe.contentDocument?.documentElement;
        if (root) {
          root.style.backgroundColor = "#ffffff";
          root.style.border = "none";
        }
        body.style.backgroundColor = "#ffffff";
        body.style.border = "none";
        body.querySelectorAll(".t-name").forEach((el) => {
          el.style.setProperty("font-size", "28px", "important");
        });
        body.querySelectorAll(".t-section").forEach((el) => {
          el.style.setProperty("color", "#333", "important");
          el.style.setProperty("font-size", "24px", "important");
          el.style.setProperty("font-weight", "700", "important");
          el.style.setProperty("border-bottom", "1px solid #333", "important");
          el.style.setProperty("margin-top", "15px", "important");
          el.style.setProperty("padding-bottom", "5px", "important");
        });
        body.querySelectorAll(".t-role").forEach((el) => {
          el.style.setProperty("color", "#0056b3", "important");
          el.style.setProperty("font-size", "20px", "important");
          el.style.setProperty("font-weight", "700", "important");
          el.style.setProperty("margin", "10px 0", "important");
        });
        body.querySelectorAll(".t-company").forEach((el) => {
          el.style.setProperty("color", "#333", "important");
          el.style.setProperty("font-weight", "700", "important");
        });
        body.querySelectorAll(".t-body, .t-bullet").forEach((el) => {
          el.style.setProperty("font-size", "16px", "important");
        });
        body.querySelectorAll(".t-meta").forEach((el) => {
          el.style.setProperty("font-size", "16px", "important");
          if (el.closest(".experience-item")) {
            el.style.setProperty("color", "#333", "important");
          } else {
            el.style.setProperty("color", "#666", "important");
          }
        });
        body.querySelectorAll(".t-meta-line").forEach((el) => {
          el.style.setProperty("color", "#333", "important");
        });
        body.querySelectorAll(".resume-header").forEach((el) => {
          el.style.setProperty("margin-bottom", "20px", "important");
        });
        body.querySelectorAll(".resume-section").forEach((el) => {
          el.style.setProperty("margin-bottom", "15px", "important");
          el.style.setProperty("margin-top", "15px", "important");
        });
        body.querySelectorAll(".experience-item").forEach((el) => {
          el.style.setProperty("margin-bottom", "10px", "important");
        });
        await html2pdf().set({
          margin: 15,
          filename,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { backgroundColor: "#ffffff", scale: 1.5 },
          pagebreak: { mode: "css", avoid: [".experience-item"] },
        }).from(body).save();
        return true;
      } catch (err) {
        console.error("PDF fallback error:", err);
        alert(formatApiErrorMessage(err, "Failed to generate PDF. You can download as HTML and print to PDF from your browser."));
        return false;
      } finally {
        if (iframe && iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      }
    },
    confirmUnsave(job) {
      this.jobToDelete = job;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.jobToDelete = null;
    },
    async deleteJob() {
      if (!this.jobToDelete) return;
      
      this.deleting = true;
      try {
        await axios.delete(`/api/jobs/${this.jobToDelete._id}`);
        this.closeDeleteModal();
        this.$emit('refresh-jobs');
      } catch (error) {
        console.error('Delete error:', error);
        alert(formatApiErrorMessage(error, 'Failed to delete job. Please try again.'));
      } finally {
        this.deleting = false;
      }
    },
    async updateJobStatus(job, newStatus) {
      if (newStatus === 'resume_generated' && !job?.hasResume) return;
      const prev = this.effectiveStatus(job);
      if (prev === newStatus) return;
      try {
        await axios.patch(`/api/jobs/${job._id}/status`, { status: newStatus });
        this.$emit('update-job-status', { jobId: job._id, status: newStatus });
      } catch (error) {
        console.error('Update status error:', error);
        alert(formatApiErrorMessage(error, 'Failed to update status. Please try again.'));
      }
    },
    openNotesModal(job) {
      this.clearNotesTimers();
      this.notesJob = job;
      this.notesText = job.notes || "";
      this.notesInitial = job.notes || "";
      this.notesSaveError = "";
      this.notesSavedFlash = false;
      this.showNotesModal = true;
      this.$nextTick(() => {
        const el = this.$refs.notesTextarea;
        if (el?.focus) {
          el.focus();
          const len = el.value?.length ?? 0;
          try {
            el.setSelectionRange(len, len);
          } catch (_) {
            /* ignore */
          }
        }
      });
    },
    clearNotesTimers() {
      if (this.notesSaveTimer) {
        clearTimeout(this.notesSaveTimer);
        this.notesSaveTimer = null;
      }
      if (this.notesSavedFlashTimer) {
        clearTimeout(this.notesSavedFlashTimer);
        this.notesSavedFlashTimer = null;
      }
    },
    queueNotesSave() {
      this.notesSaveError = "";
      this.notesSavedFlash = false;
      if (this.notesSaveTimer) clearTimeout(this.notesSaveTimer);
      this.notesSaveTimer = setTimeout(() => {
        this.notesSaveTimer = null;
        void this.flushNotesSave();
      }, 600);
    },
    async flushNotesSave() {
      if (!this.notesJob) return true;
      if (this.notesText === this.notesInitial) return true;

      if (this.notesSavePromise) {
        try {
          await this.notesSavePromise;
        } catch (_) {
          /* status already set */
        }
      }

      if (!this.notesJob || this.notesText === this.notesInitial) return true;

      const jobId = this.notesJob._id;
      const notes = this.notesText;
      this.savingNotes = true;
      this.notesSaveError = "";

      this.notesSavePromise = (async () => {
        try {
          await axios.patch(`/api/jobs/${jobId}/status`, { notes });
          this.$emit("update-job-notes", { jobId, notes });
          if (this.notesJob && this.notesJob._id === jobId) {
            this.notesInitial = notes;
            this.notesSavedFlash = true;
            if (this.notesSavedFlashTimer) clearTimeout(this.notesSavedFlashTimer);
            this.notesSavedFlashTimer = setTimeout(() => {
              this.notesSavedFlash = false;
              this.notesSavedFlashTimer = null;
            }, 1500);
          }
          return true;
        } catch (error) {
          console.error("Save notes error:", error);
          this.notesSaveError = formatApiErrorMessage(
            error,
            "Failed to save notes. Please try again."
          );
          return false;
        } finally {
          this.savingNotes = false;
          this.notesSavePromise = null;
        }
      })();

      return this.notesSavePromise;
    },
    async closeNotesModal() {
      if (this.notesSaveTimer) {
        clearTimeout(this.notesSaveTimer);
        this.notesSaveTimer = null;
      }
      const ok = await this.flushNotesSave();
      if (!ok && this.notesSaveError) {
        // Keep modal open so the user can retry or copy text
        return;
      }
      this.clearNotesTimers();
      this.showNotesModal = false;
      this.notesJob = null;
      this.notesText = "";
      this.notesInitial = "";
      this.notesSaveError = "";
      this.notesSavedFlash = false;
    },
  },
};
</script>

<style scoped>
.jobs-container {
  width: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Search + Refresh toolbar */
.jobs-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  margin-bottom: 12px;
  min-height: 44px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.jobs-toolbar .search-bar {
  flex: 1;
  margin-bottom: 0;
  min-height: 44px;
  height: 44px;
  padding: 0 14px;
  border-radius: 10px;
  box-sizing: border-box;
}

.jobs-toolbar .btn-refresh {
  flex-shrink: 0;
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
  box-sizing: border-box;
}

.jobs-toolbar .btn-refresh:hover:not(:disabled) {
  background: var(--app-border);
  color: var(--app-text);
  border-color: var(--input-border-hover);
}

.jobs-toolbar .btn-refresh:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.jobs-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 4px 4px;
  color: var(--app-text-secondary);
  font-size: 13px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-page {
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  border-radius: 8px;
  color: var(--app-text);
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
}

.btn-page:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pagination-page {
  min-width: 7rem;
  text-align: center;
}

.btn-search-toggle {
  display: none;
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  color: var(--app-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
}

.btn-search-toggle.active {
  color: var(--app-primary);
  border-color: var(--app-primary);
  background: color-mix(in srgb, var(--app-primary) 12%, var(--app-surface));
}

.search-toggle-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--app-primary);
}

.btn-clear-search {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--app-text-secondary);
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
}

.btn-clear-search:hover {
  color: var(--app-text);
  background: var(--app-surface-hover);
}

.sort-control {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  min-width: 0;
}

.sort-label {
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
}

.sort-select {
  min-width: 160px;
  height: 44px;
  padding: 0 28px 0 12px;
  border-radius: 10px;
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-text);
  font-size: 13px;
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2364748b' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
}

.sort-select:focus {
  outline: none;
  border-color: var(--app-border-focus);
}

/* Search Bar */
.search-bar {
  display: flex;
  align-items: center;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 12px;
  padding: 12px 16px;
  transition: all 0.2s;
}

.search-bar:focus-within {
  border-color: var(--app-border-focus);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--app-primary) 15%, transparent);
}

.search-icon {
  color: var(--text-muted);
  margin-right: 12px;
  font-size: 14px;
}

.search-bar input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--app-text);
  font-size: 14px;
}

.search-bar input::placeholder {
  color: var(--text-muted);
}

/* Empty State - fill remaining space so layout isn't too small */
.empty-state {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 80px 20px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15));
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.empty-icon i {
  font-size: 32px;
  color: #5b21b6;
}

.empty-state h3 {
  color: var(--app-text);
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.empty-state p {
  color: var(--text-muted);
  font-size: 14px;
  max-width: 360px;
  margin: 0 auto;
  line-height: 1.6;
}

.table-scroll-caption {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin: -12px 0 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: var(--text-secondary);
  background: color-mix(in srgb, var(--primary) 8%, var(--surface-2));
  border: 1px solid color-mix(in srgb, var(--primary) 20%, var(--border));
  border-radius: 8px;
  flex-shrink: 0;
}

.caption-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.caption-item i {
  color: var(--primary);
  font-size: 11px;
}

.caption-divider {
  color: var(--text-muted);
  opacity: 0.6;
}

/* Table - takes remaining height and scrolls so grid doesn't overflow */
.table-scroll-region {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 12px;
}

.table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
  border-radius: 0 0 12px 12px;
}

.table-wrapper::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: var(--surface-3);
  border-radius: 5px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: var(--border);
  border-radius: 5px;
  border: 2px solid var(--surface-3);
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}

.table-scroll-region.has-overflow-y .table-wrapper {
  box-shadow: inset 0 -12px 12px -12px color-mix(in srgb, var(--text-primary) 12%, transparent);
}

.scroll-edge {
  position: absolute;
  pointer-events: none;
  z-index: 3;
}

.scroll-edge-bottom {
  left: 0;
  right: 10px;
  bottom: 0;
  height: 48px;
  background: linear-gradient(
    to bottom,
    transparent,
    color-mix(in srgb, var(--surface-2) 70%, transparent)
  );
  border-radius: 0 0 12px 12px;
}

.scroll-edge-right {
  top: 44px;
  right: 0;
  bottom: 10px;
  width: 56px;
  background: linear-gradient(
    to left,
    color-mix(in srgb, var(--surface-2) 92%, transparent),
    transparent
  );
}

.scroll-edge-left {
  top: 44px;
  left: 0;
  bottom: 10px;
  width: 40px;
  background: linear-gradient(
    to right,
    color-mix(in srgb, var(--surface-2) 90%, transparent),
    transparent
  );
}

.scroll-hint {
  position: absolute;
  z-index: 4;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
  box-shadow: var(--shadow-modal);
}

.scroll-hint-down {
  left: 50%;
  bottom: 14px;
  transform: translateX(-50%);
  animation: scroll-hint-bounce-y 2s ease-in-out infinite;
}

.scroll-hint-side {
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  animation: scroll-hint-bounce-x 2s ease-in-out infinite;
}

.scroll-hint:hover {
  color: var(--primary);
  border-color: color-mix(in srgb, var(--primary) 40%, var(--border));
}

@keyframes scroll-hint-bounce-y {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(4px); }
}

@keyframes scroll-hint-bounce-x {
  0%, 100% { transform: translateY(-50%) translateX(0); }
  50% { transform: translateY(-50%) translateX(4px); }
}

.jobs-table {
  width: 100%;
  min-width: 1040px;
  border-collapse: collapse;
  border: none;
}

.jobs-table th,
.jobs-table td {
  border: none;
  outline: none;
}

.jobs-table thead {
  position: sticky;
  top: 0;
  z-index: 2;
}

.jobs-table th {
  text-align: left;
  padding: 12px 16px 16px;
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: var(--surface-2);
  box-shadow: 0 1px 0 var(--border);
}

.table-wrapper.is-scrolled-y .jobs-table th {
  box-shadow: 0 4px 8px -4px color-mix(in srgb, var(--text-primary) 15%, transparent);
}

.jobs-table th.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.jobs-table th.sortable:hover {
  color: var(--app-text-secondary);
}

.jobs-table th.sortable.active {
  color: var(--app-primary);
}

.jobs-table th .sort-icon {
  margin-left: 4px;
  font-size: 10px;
  opacity: 0.7;
}

.jobs-table th.sortable.active .sort-icon {
  opacity: 1;
}

.job-row {
  background: transparent;
  transition: all 0.2s ease;
}

.job-row:hover {
  background: var(--app-surface-hover);
}

.job-row:hover td {
  background: var(--app-surface-hover);
}

.job-row td {
  padding: 18px 16px;
  vertical-align: middle;
  background: transparent;
  border: none !important;
  border-top: 1px solid var(--app-border) !important;
}

/* Table Cells */
.job-title-cell {
  min-width: 220px;
}

.job-title {
  display: block;
  color: var(--app-text);
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 3px;
}

.job-location {
  display: block;
  color: var(--text-muted);
  font-size: 12px;
}

.company-cell {
  color: var(--app-text-secondary);
  font-size: 14px;
  font-weight: 500;
}

/* Match Score Column */
.match-cell {
  white-space: nowrap;
}

.match-badge-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  cursor: pointer;
}

.match-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.match-badge.match-strong {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.match-badge.match-good {
  background: rgba(234, 179, 8, 0.15);
  color: #eab308;
}

.match-badge.match-fair {
  background: rgba(249, 115, 22, 0.15);
  color: #f97316;
}

.match-badge.match-weak {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.match-verdict {
  font-size: 10px;
  color: var(--text-muted);
  padding-left: 2px;
}

.match-pending {
  display: flex;
  align-items: center;
  justify-content: center;
}

.match-loading {
  color: var(--text-muted);
  font-size: 14px;
}

.match-unavailable {
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
}

/* Match Popout */
.match-popout {
  position: fixed;
  z-index: 9999;
  box-sizing: border-box;
  background: var(--app-surface);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--app-border);
  border-radius: 16px;
  padding: 20px;
  min-width: 320px;
  max-width: 380px;
  /* height/maxHeight set inline; border-box so padding is included and popout fits in viewport */
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-modal);
  overflow: hidden;
  min-height: 0;
}

.popout-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--app-border);
}

.popout-body {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  margin: 0 -4px 0 0;
  padding-right: 4px;
}

.popout-body::-webkit-scrollbar {
  width: 6px;
}

.popout-body::-webkit-scrollbar-track {
  background: var(--app-surface-hover);
  border-radius: 3px;
}

.popout-body::-webkit-scrollbar-thumb {
  background: var(--app-border);
  border-radius: 3px;
}

.popout-body::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}

.popout-score {
  font-size: 32px;
  font-weight: 700;
}

.popout-score.match-strong { color: #22c55e; }
.popout-score.match-good { color: #eab308; }
.popout-score.match-fair { color: #f97316; }
.popout-score.match-weak { color: #ef4444; }

.popout-verdict {
  font-size: 14px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
}

.popout-verdict.match-strong {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.popout-verdict.match-good {
  background: rgba(234, 179, 8, 0.15);
  color: #eab308;
}

.popout-verdict.match-fair {
  background: rgba(249, 115, 22, 0.15);
  color: #f97316;
}

.popout-verdict.match-weak {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.popout-categories {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.popout-category {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.category-name {
  font-size: 13px;
  color: var(--app-text-secondary);
}

.category-score {
  font-size: 13px;
  font-weight: 600;
  color: var(--app-text);
}

.category-bar {
  height: 6px;
  background: var(--app-surface-hover);
  border-radius: 3px;
  overflow: hidden;
}

.category-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.popout-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--app-border);
}

.popout-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted);
  margin-bottom: 10px;
}

.popout-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 8px;
}

.popout-item i {
  margin-top: 2px;
  font-size: 11px;
}

.popout-item.highlight {
  color: var(--app-text);
}

.popout-item.highlight i {
  color: #22c55e;
}

.popout-item.gap {
  color: var(--app-text-secondary);
}

.popout-item.gap i {
  color: #f97316;
}

/* Mobile Match Badge */
.mobile-match {
  cursor: pointer;
}

/* Mobile Match Modal */
.match-modal {
  max-width: 400px;
  width: calc(100% - 32px);
}

.match-modal-content {
  padding-top: 16px;
}

.match-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--app-border);
}

.match-modal-score {
  font-size: 36px;
  font-weight: 700;
}

.match-modal-score.match-strong { color: #22c55e; }
.match-modal-score.match-good { color: #eab308; }
.match-modal-score.match-fair { color: #f97316; }
.match-modal-score.match-weak { color: #ef4444; }

.match-modal-verdict {
  font-size: 14px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
}

.match-modal-verdict.match-strong {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.match-modal-verdict.match-good {
  background: rgba(234, 179, 8, 0.15);
  color: #eab308;
}

.match-modal-verdict.match-fair {
  background: rgba(249, 115, 22, 0.15);
  color: #f97316;
}

.match-modal-verdict.match-weak {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.match-modal-categories {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
}

.match-modal-category {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.match-modal-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--app-border);
}

.match-modal-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted);
  margin-bottom: 12px;
}

.match-modal-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 10px;
}

.match-modal-item i {
  margin-top: 3px;
  font-size: 12px;
}

.match-modal-item.highlight {
  color: var(--app-text);
}

.match-modal-item.highlight i {
  color: #22c55e;
}

.match-modal-item.gap {
  color: var(--app-text-secondary);
}

.match-modal-item.gap i {
  color: #f97316;
}

/* Popout Animation */
.popout-enter-active,
.popout-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.popout-enter-from,
.popout-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.date-cell {
  color: var(--text-muted);
  font-size: 13px;
  white-space: nowrap;
}

/* Salary */
.salary-cell {
  white-space: nowrap;
}

.salary-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.no-salary {
  color: var(--text-muted);
}

/* Job Type */
.type-cell {
  white-space: nowrap;
}

.job-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(139, 92, 246, 0.12);
  color: #6d28d9;
  text-transform: capitalize;
}

.no-type {
  color: var(--text-muted);
}

/* Applied Checkbox */
.status-cell {
  min-width: 108px;
}

.status-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.status-field-label {
  font-size: 11px;
  color: var(--text-secondary);
}

.status-select {
  width: 100%;
  min-width: 100px;
  height: 44px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid var(--input-border);
  background: var(--input-bg);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  box-sizing: border-box;
}

.status-select:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.status-select.status-applied {
  border-color: color-mix(in srgb, var(--primary) 50%, var(--border));
}

.status-select.status-interviewing {
  border-color: color-mix(in srgb, #eab308 50%, var(--border));
}

.status-select.status-offer {
  border-color: color-mix(in srgb, var(--success) 50%, var(--border));
}

.status-select.status-rejected {
  border-color: var(--border);
}

.status-select.status-skipped {
  border-color: var(--border);
}

.mobile-status {
  margin-right: 0;
  flex: 1;
  min-width: 0;
}

.applied-checkbox {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  position: relative;
}

.applied-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.applied-checkbox .checkmark {
  width: 20px;
  height: 20px;
  background: var(--app-surface);
  border: 2px solid var(--app-border);
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.applied-checkbox .checkmark::after {
  content: '\f00c';
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  font-size: 10px;
  color: transparent;
  transition: all 0.2s;
}

.applied-checkbox input:checked + .checkmark {
  background: rgba(34, 197, 94, 0.15);
  border-color: #22c55e;
}

.applied-checkbox input:checked + .checkmark::after {
  color: #22c55e;
}

.applied-checkbox:hover .checkmark {
  border-color: var(--input-border-hover);
}

.mobile-applied {
  gap: 8px;
}

.mobile-applied .checkbox-label {
  font-size: 12px;
  color: var(--app-text-secondary);
}

.mobile-card-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--app-border);
}

.mobile-card-row .btn-notes-icon {
  /* Sit on same baseline as the 44px select (under the Status label) */
  align-self: flex-end;
  margin-bottom: 0;
}

/* Notes */
.notes-cell {
  text-align: center;
}

.btn-notes {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  font-size: 16px;
  color: var(--app-text-secondary);
}

.btn-notes:hover {
  background: var(--app-surface-hover);
  color: var(--app-primary);
}

.btn-notes i {
  color: inherit;
}

.btn-notes-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s;
  font-size: 16px;
  color: var(--app-text-secondary);
  box-sizing: border-box;
}

.btn-notes-icon.has-notes {
  color: var(--app-primary);
  border-color: color-mix(in srgb, var(--app-primary) 35%, var(--app-border));
  background: color-mix(in srgb, var(--app-primary) 8%, var(--app-surface));
}

.btn-notes-icon:hover {
  border-color: var(--app-primary);
  color: var(--app-primary);
  background: color-mix(in srgb, var(--app-primary) 10%, var(--app-surface));
}

.btn-notes-icon i {
  color: inherit;
}

/* Notes sheet — compact, Apple-like */
.notes-overlay {
  align-items: flex-end !important;
  justify-content: center;
  padding: 0 !important;
  background: rgba(0, 0, 0, 0.32);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.notes-sheet {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 16px;
  padding: 8px 16px 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 0 auto;
  height: auto;
  align-self: flex-end;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

.notes-sheet-handle {
  display: none;
  width: 36px;
  height: 5px;
  border-radius: 100px;
  background: color-mix(in srgb, var(--app-text) 18%, transparent);
  margin: 4px auto 2px;
}

.notes-sheet-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  gap: 12px;
}

.notes-sheet-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--app-text);
}

.notes-done-btn {
  min-width: 44px;
  min-height: 44px;
  padding: 0 4px 0 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--app-primary);
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.notes-done-btn:hover:not(:disabled) {
  opacity: 0.75;
}

.notes-done-btn:disabled {
  opacity: 0.55;
  cursor: wait;
}

.notes-sheet-context {
  margin: 0;
  font-size: 13px;
  line-height: 1.35;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notes-sheet-job {
  color: var(--app-text-secondary);
  font-weight: 500;
}

.notes-sheet-sep {
  margin: 0 4px;
}

.notes-sheet-input {
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 10px 0 4px;
  border: none;
  border-top: 1px solid var(--app-border);
  border-radius: 0;
  background: transparent;
  color: var(--app-text);
  font-size: 16px;
  line-height: 1.45;
  font-family: inherit;
  resize: none;
  min-height: 88px;
  max-height: 40vh;
  outline: none;
  box-shadow: none;
  field-sizing: content; /* grow with text where supported */
}

.notes-sheet-input::placeholder {
  color: var(--text-muted);
}

.notes-sheet-input:focus {
  outline: none;
  box-shadow: none;
}

.notes-save-status {
  margin: 0;
  font-size: 12px;
  color: var(--text-muted);
  min-height: 0;
}

.notes-save-status.error {
  color: var(--app-error, #dc2626);
}

@media (max-width: 768px) {
  .notes-overlay {
    align-items: flex-end;
    justify-content: stretch;
    padding: 0;
  }

  .notes-sheet {
    max-width: none;
    width: 100%;
    max-height: min(70vh, 520px);
    border-radius: 14px 14px 0 0;
    border-left: none;
    border-right: none;
    border-bottom: none;
    padding: 6px 16px calc(10px + env(safe-area-inset-bottom, 0px));
    box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.12);
    animation: notes-sheet-up 0.28s cubic-bezier(0.32, 0.72, 0, 1);
    /* Hug content — don’t stretch to fill the overlay */
    align-self: flex-end;
    flex: 0 0 auto;
    height: auto;
  }

  .notes-sheet-handle {
    display: block;
  }

  .notes-sheet-title {
    font-size: 16px;
  }

  .notes-done-btn {
    font-size: 16px;
  }

  .notes-sheet-input {
    min-height: 72px;
    max-height: min(32vh, 200px);
    overflow-y: auto;
    font-size: 16px;
  }
}

@keyframes notes-sheet-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@media (min-width: 769px) {
  .notes-overlay {
    align-items: center !important;
    padding: 24px !important;
  }

  .notes-sheet {
    border-radius: 14px;
    padding: 10px 18px 18px;
    flex: 0 0 auto;
    height: auto;
    align-self: center;
  }

  .notes-sheet-handle {
    display: none;
  }

  .notes-sheet-input {
    min-height: 96px;
    max-height: 280px;
    resize: none;
    overflow-y: auto;
  }
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-saved {
  background: rgba(59, 130, 246, 0.1);
  color: #60a5fa;
}
.status-saved .status-dot {
  background: #60a5fa;
}

.status-resume_generated {
  background: rgba(139, 92, 246, 0.1);
  color: #a78bfa;
}
.status-resume_generated .status-dot {
  background: #a78bfa;
}

.status-applied {
  background: rgba(34, 197, 94, 0.1);
  color: #4ade80;
}
.status-applied .status-dot {
  background: #4ade80;
}

.status-interviewing {
  background: rgba(251, 191, 36, 0.1);
  color: #fbbf24;
}
.status-interviewing .status-dot {
  background: #fbbf24;
}

.status-rejected {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
}
.status-rejected .status-dot {
  background: #f87171;
}

.status-skipped {
  background: rgba(148, 163, 184, 0.15);
  color: #94a3b8;
}
.status-skipped .status-dot {
  background: #94a3b8;
}

.status-offer {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}
.status-offer .status-dot {
  background: #22c55e;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  white-space: nowrap;
}

.btn-generate {
  background: color-mix(in srgb, var(--app-primary) 16%, var(--app-surface));
  color: var(--app-primary);
  min-width: 150px;
  border-radius: 10px;
  font-weight: 600;
  border: none;
}

.btn-generate:hover:not(:disabled) {
  background: color-mix(in srgb, var(--app-primary) 24%, var(--app-surface));
  color: var(--app-primary-hover);
}

.btn-generate:disabled {
  opacity: 0.55;
}

.btn-download {
  background: color-mix(in srgb, var(--app-primary) 10%, var(--app-surface));
  color: var(--app-primary);
  border: none;
  min-width: 150px;
  border-radius: 10px;
  font-weight: 600;
}

.btn-download:hover {
  background: color-mix(in srgb, var(--app-primary) 16%, var(--app-surface));
  color: var(--app-primary-hover);
}

.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--app-surface-hover);
  color: var(--app-text-secondary);
  text-decoration: none;
  transition: all 0.2s;
  border: 1px solid var(--app-border);
}

.btn-icon:hover {
  background: var(--app-border);
  color: var(--app-text);
}

/* Star/Save Button */
.save-col {
  width: 50px;
}

.save-cell {
  width: 50px;
  text-align: center;
}

.btn-star {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  font-size: 16px;
}

.btn-star.saved {
  color: #fbbf24;
}

.btn-star.saved:hover {
  color: rgba(251, 191, 36, 0.5);
  background: rgba(251, 191, 36, 0.1);
}

/* Source Badge */
.source-cell {
  white-space: nowrap;
}

.source-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.source-linkedin {
  background: rgba(10, 102, 194, 0.15);
  color: #60a5fa;
}

.source-indeed {
  background: rgba(45, 95, 232, 0.15);
  color: #818cf8;
}

.source-glassdoor {
  background: rgba(12, 170, 65, 0.15);
  color: #4ade80;
}

.source-web {
  background: var(--app-surface-hover);
  color: var(--app-text-secondary);
}

/* URL Cell */
.url-cell {
  text-align: center;
}

.no-url {
  color: var(--text-muted);
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--app-overlay);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
  box-sizing: border-box;
  overflow-y: auto;
}

.glass-modal {
  background: var(--app-surface);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border: 1px solid var(--app-border);
  border-radius: 20px;
  padding: 28px;
  width: 100%;
  max-width: 360px;
  margin: 20px;
  box-shadow: var(--shadow-modal);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.modal-header h3 {
  color: var(--app-text);
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  color: var(--app-text-secondary);
  font-size: 16px;
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 8px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: var(--app-border);
  color: var(--app-text);
}

.modal-subtitle {
  color: var(--app-text);
  font-size: 15px;
  margin-bottom: 4px;
}

.modal-company {
  color: var(--app-text-secondary);
  font-size: 13px;
  margin-bottom: 24px;
}

.download-options {
  display: flex;
  gap: 12px;
}

.download-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 16px;
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.download-btn:hover:not(:disabled) {
  background: var(--app-border);
  border-color: var(--app-border-focus);
  transform: translateY(-2px);
}

.download-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.download-btn.is-loading .download-icon {
  opacity: 0.9;
}

.download-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.download-icon.pdf {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.download-icon.docx {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
}

.download-icon.html {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
}

.download-btn span {
  font-size: 12px;
  font-weight: 500;
  color: var(--app-text-secondary);
}

/* Delete Modal */
.delete-warning {
  color: var(--app-text-secondary);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 24px;
}

.modal-actions {
  display: flex;
  gap: 12px;
}

.btn-cancel {
  flex: 1;
  background: var(--app-surface-hover);
  color: var(--app-text-secondary);
  border: 1px solid var(--app-border);
}

.btn-cancel:hover {
  background: var(--app-border);
  color: var(--app-text);
}

.btn-confirm-delete {
  flex: 1;
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.btn-confirm-delete:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.25);
}

.btn-confirm-delete:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 900px) {
  .jobs-table {
    min-width: 960px;
  }
}

@media (max-width: 768px) {
  .table-scroll-caption {
    display: none;
  }

  /* Hide table on mobile */
  .table-scroll-region {
    display: none;
  }

  /* Show mobile card view */
  .jobs-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .search-bar {
    margin-bottom: 0;
  }

  /* Toolbar sits with the list — even padding above/below controls */
  .jobs-toolbar {
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 0;
    margin-bottom: 10px;
    min-height: 0;
  }

  .jobs-toolbar.is-search-collapsed {
    flex-wrap: nowrap;
    align-items: center;
  }

  .btn-search-toggle {
    display: flex;
  }

  .jobs-toolbar .search-bar {
    flex: 1 1 100%;
    min-width: 0;
    order: 20;
  }

  .jobs-toolbar .sort-control {
    flex: 1 1 auto;
    min-width: 0;
    height: 44px;
  }

  .jobs-toolbar .sort-select {
    min-width: 0;
    width: 100%;
    max-width: none;
    flex: 1;
    height: 44px;
    max-height: 44px;
    min-height: 44px;
    padding-top: 0;
    padding-bottom: 0;
    line-height: 44px;
  }

  .jobs-toolbar .sort-label {
    display: none;
  }

  .jobs-toolbar .btn-search-toggle,
  .jobs-toolbar .btn-refresh {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    max-height: 44px;
    min-height: 44px;
  }

  .jobs-toolbar .btn-refresh {
    margin-left: 0;
  }
}

/* Mobile Cards - shown via JS on mobile */
.mobile-jobs-list {
  display: none;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

@media (max-width: 768px) {
  .mobile-jobs-list {
    display: flex;
    gap: 12px;
    padding: 6px 4px 24px;
    margin: 0 -2px;
    background: transparent;
    border: none;
    box-shadow: none;
    -webkit-overflow-scrolling: touch;
  }

  .mobile-job-card {
    background: var(--app-surface);
    border: 1px solid var(--app-border);
    border-radius: var(--card-radius, 12px);
    box-shadow: var(--shadow-card);
    padding: 14px;
    flex-shrink: 0;
  }

  .mobile-job-card:hover {
    background: var(--app-surface);
  }

  /* Flat toolbar — elevation reserved for job cards */
  .jobs-toolbar .btn-search-toggle,
  .jobs-toolbar .btn-refresh,
  .jobs-toolbar .sort-select,
  .jobs-toolbar .search-bar {
    background: var(--app-surface);
    border: 1px solid var(--app-border);
    box-shadow: none;
  }

  .jobs-toolbar .btn-search-toggle.active {
    background: color-mix(in srgb, var(--app-primary) 10%, var(--app-surface));
    border-color: color-mix(in srgb, var(--app-primary) 28%, var(--app-border));
    box-shadow: none;
  }

  /* Uniform badge typography on mobile cards */
  .mobile-card-badges .match-badge,
  .mobile-card-badges .salary-badge,
  .mobile-card-badges .job-type-badge,
  .mobile-card-badges .source-badge,
  .mobile-card-meta .source-badge,
  .mobile-salary,
  .mobile-job-type {
    font-size: 11px !important;
    font-weight: 600 !important;
    line-height: 1.2;
    padding: 4px 8px;
    border-radius: 8px;
  }
}

.mobile-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.mobile-card-title {
  flex: 1;
}

.mobile-card-title h4 {
  color: var(--app-text);
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 4px 0;
}

.mobile-card-title p {
  color: var(--app-text-secondary);
  font-size: 13px;
  margin: 0;
}

.mobile-card-star {
  color: #fbbf24;
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
}

.mobile-card-star:hover {
  opacity: 0.7;
}

.mobile-card-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}

.mobile-card-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.mobile-card-meta .status-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
}

.mobile-card-meta .date-text {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.3;
}

.mobile-card-actions {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.mobile-card-actions .btn {
  flex: 1;
  min-height: 44px;
  padding: 10px 12px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.mobile-card-actions .btn-view-job {
  flex: 0 0 auto;
  min-width: 88px;
  gap: 6px;
  background: var(--app-surface);
  color: var(--app-text-secondary);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
}

.mobile-card-actions .btn-view-job:hover {
  background: var(--app-surface-hover);
  color: var(--app-text);
}

.mobile-card-actions .btn-icon {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
}

/* Mobile modal adjustments */
@media (max-width: 480px) {
  .glass-modal {
    padding: 20px;
    margin: 12px;
    border-radius: 16px;
  }

  .download-options {
    flex-direction: row;
    gap: 8px;
  }

  .download-btn {
    padding: 14px 10px;
  }

  .download-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .modal-actions {
    flex-direction: column;
  }

  .modal-actions .btn {
    width: 100%;
  }
}
</style>
